import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Parse from 'parse';

const DeleteOperatorDialog = ({ open, onClose, onDelete, operator }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      await Parse.Cloud.run('deleteOperator', { operatorId: operator.id });
      onDelete();
      onClose();
    } catch (err) {
      console.error('Error deleting operator:', err);
      setError(err.message || 'Failed to delete operator');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#242424', m: 0 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
          backgroundColor: '#242424',
          color: '#F44336',
          pb: 1
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 24, color: '#F44336' }} />
        </Box>
        <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>Delete Operator</Typography>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: '#242424', textAlign: 'left', py: 2 }}>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
          Are you sure you want to delete the operator{' '}
          <Typography component="span" sx={{ color: '#FFC107', fontWeight: 600 }}>
            {operator?.name}
          </Typography>
          ?
        </Typography>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '12px', mt: 1 }}>
          This action cannot be undone. All associated domains will also be deleted.
        </Typography>
        {error && (
          <Typography sx={{ color: '#F44336', fontSize: '14px', mt: 2 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: '#242424', px: 3, pb: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          fullWidth
          disabled={loading}
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.3)',
            color: '#fff',
            borderRadius: '4px',
            py: 1,
            fontSize: '14px',
            textTransform: 'capitalize',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            backgroundColor: '#F44336',
            color: '#fff',
            borderRadius: '4px',
            py: 1,
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'capitalize',
            '&:hover': { backgroundColor: '#D32F2F' }
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteOperatorDialog;
